import { ReactElement, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { v4 as uuid } from 'uuid'
import { LoadingButton } from '@mui/lab'
import { Button, List, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import { useResultsInvalidation } from '@editor/hooks'
import {
  DEFAULT_LINE_LIKE_SETTINGS,
  wallSettingsOf,
  WallSettings,
} from '@editor/pages/StructuralPlanning/components/PlanSettingsForm/utils'
import { ImmutableVector3, getCenterMultipleReduceMean } from '@modugen/ui-toolkit'
import { useMutation } from '@tanstack/react-query'
import { SwitchLabeled } from '@ui/actions'
import DialogBase from '@ui/feedback/DialogBase'
import { CoordinatePointInput, Errors, Form } from '@ui/forms'
import { SplitElementIcon } from '@ui/icons'
import { closeSnackbar, enqueueSnackbar, SnackbarKey } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { getModel, getVerticalTransmissionGraph } from '@queries'
import { createOpening, createRips } from '@mutations'
import { buildErrorMessage } from 'src/constants/errors'
import queryClient from 'src/state/client'
import { splitWallSchema } from './schema'
import { splitWall } from './utils'

interface Props {
  element_guid: string
  handleCreate: (
    wall: {
      p1: { x: number; y: number }
      p2: { x: number; y: number }
      storey_idx: number
      placement: WallPlacement
      openings: Opening[]
    } & WallSettings,
  ) => Promise<ShapeObject>
  handleDelete: (wallGuid: string) => Promise<void>
}

interface SplitFormValues {
  splitPoint: Point
  wall: ShapeObject
}

interface SplitWallFormFieldsProps {
  isLoading: boolean
}

const SplitWallFormFields = ({ isLoading }: SplitWallFormFieldsProps) => {
  const [limitXYDecimals, setLimitXYDecimals] = useState(true)
  const [limitZDecimals, setLimitZDecimals] = useState(false)
  const pointManipulation = useMemo(
    () => ({
      x: { roundOnReplace: limitXYDecimals },
      y: { roundOnReplace: limitXYDecimals },
      z: { roundOnReplace: limitZDecimals },
    }),
    [limitXYDecimals, limitZDecimals],
  )

  return (
    <>
      <Stack spacing={1} alignContent={'center'} direction="row">
        <CoordinatePointInput
          label="Teilungspunkt"
          name="splitPoint"
          pointManipulation={pointManipulation}
          actions={
            <>
              <Stack direction="row" spacing={0.5} alignItems={'center'}>
                <Typography>Auf 2 Dez. begrenzen:</Typography>
                <SwitchLabeled
                  checked={limitXYDecimals}
                  onChange={event => {
                    setLimitXYDecimals(event.target.checked)
                  }}
                  label="XY"
                  sx={{ padding: 0 }}
                />
                <SwitchLabeled
                  checked={limitZDecimals}
                  onChange={event => {
                    setLimitZDecimals(event.target.checked)
                  }}
                  label="Z"
                  sx={{ padding: 0 }}
                />
              </Stack>
            </>
          }
        />
        <LoadingButton
          type="submit"
          variant="contained"
          color="primary"
          size="small"
          loading={isLoading}
        >
          <SplitElementIcon />
        </LoadingButton>
      </Stack>
    </>
  )
}

const SplitWallForm = ({ element_guid, handleCreate, handleDelete }: Props): ReactElement => {
  const { walls, rips } = useModelStore(state => state.model)
  const invalidateResults = useResultsInvalidation()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [pendingFormData, setPendingFormData] = useState<SplitFormValues | null>(null)

  // memos
  const selectedWall = useMemo(() => {
    return walls.find(wall => wall.guid === element_guid)
  }, [walls, element_guid])

  const defaultValues = useMemo(
    () => ({
      splitPoint: selectedWall
        ? getCenterMultipleReduceMean(selectedWall.shape.points)
        : { x: 0, y: 0, z: 0 },
      wall: selectedWall,
    }),
    [selectedWall],
  )

  const { projectId }: { projectId?: string } = useParams()

  const { mutate, isLoading } = useMutation(
    async (formData: SplitFormValues) => {
      const snackbarWaitItemId = `split-wall-form-${element_guid}-pending-notification`
      enqueueSnackbar('Wand wird geteilt. Dies kann einige Sekunden dauern.', {
        variant: 'info',
        persist: true,
        key: snackbarWaitItemId,
      })
      const splitPoint = new ImmutableVector3(
        formData.splitPoint.x,
        formData.splitPoint.y,
        formData.splitPoint.z,
      )
      const splitData = splitWall(formData.wall as ShapeObject, splitPoint, rips)

      // remove old wall
      await handleDelete(element_guid)

      // create new walls and their openings
      const newWall1 = await handleCreate({
        ...splitData.newWall1,
        ...wallSettingsOf(selectedWall),
      })

      // Create openings for wall1
      for (const opening of splitData.newWall1.openings) {
        await createOpening.request(projectId as string, {
          element_guid: newWall1.guid,
          shape: opening.shape,
        })
      }

      // Create additional rips on wall1
      if (splitData.newWall1.additionalRips.length > 0) {
        const ripRequests: WallRipRequest[] = splitData.newWall1.additionalRips.map(rip => ({
          ...DEFAULT_LINE_LIKE_SETTINGS,
          guid: uuid(),
          wall_guid: newWall1.guid,
          relative_position: rip.relative_position,
          rip_type: 'AdditionalRip',
        }))
        await createRips.request(projectId as string, ripRequests)
      }

      const newWall2 = await handleCreate({
        ...splitData.newWall2,
        ...wallSettingsOf(selectedWall),
      })

      // Create openings for wall2
      for (const opening of splitData.newWall2.openings) {
        await createOpening.request(projectId as string, {
          element_guid: newWall2.guid,
          shape: opening.shape,
        })
      }

      // Create additional rips on wall2
      if (splitData.newWall2.additionalRips.length > 0) {
        const ripRequests: WallRipRequest[] = splitData.newWall2.additionalRips.map(rip => ({
          ...DEFAULT_LINE_LIKE_SETTINGS,
          guid: uuid(),
          wall_guid: newWall2.guid,
          relative_position: rip.relative_position,
          rip_type: 'AdditionalRip',
        }))
        await createRips.request(projectId as string, ripRequests)
      }

      return { newWall1, newWall2, snackbarWaitItemId }
    },
    {
      onSuccess: async (data: { snackbarWaitItemId: SnackbarKey }) => {
        closeSnackbar(data.snackbarWaitItemId)
        await Promise.all([
          queryClient.invalidateQueries(getModel.getKey(projectId)),
          queryClient.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          queryClient.invalidateQueries(getModel.getKey(projectId)),
          queryClient.invalidateQueries(getModel.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Wand erfolgreich geteilt', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        const snackbarWaitItemId = `split-wall-form-${element_guid}-pending-notification`
        closeSnackbar(snackbarWaitItemId)
        const errorMessage = buildErrorMessage(error, 'Fehler beim Teilen der Wand')
        enqueueSnackbar(errorMessage, { variant: 'error' })
      },
    },
  )

  const handleSubmit = (values: SplitFormValues) => {
    // Show confirmation dialog instead of submitting directly
    setPendingFormData(values)
    setShowConfirmDialog(true)
  }

  const handleConfirm = () => {
    if (pendingFormData) {
      setShowConfirmDialog(false)
      mutate(pendingFormData)
      setPendingFormData(null)
    }
  }

  const handleCancel = () => {
    setShowConfirmDialog(false)
    setPendingFormData(null)
  }

  const sideEffects = [
    'Hinzugefügte Lasten',
    'Lastweiterleitung (Vertikal und Zug)',
    'Aufbau und QS Einstellungen (Standards werden wiederhergestellt)',
    'Einstellungen an Auflagern (Standards werden wiederhergestellt)',
    'Bauteilnamen werden neu vergeben',
    'Windlasten werden zurückgesetzt',
  ]

  if (!selectedWall) {
    return <></>
  }

  return (
    <>
      <Form
        id={'SplitWallForm'}
        key={element_guid}
        defaultValues={defaultValues}
        validationSchema={splitWallSchema}
        onSubmit={handleSubmit}
      >
        <SplitWallFormFields isLoading={isLoading} />
        <Errors />
      </Form>

      <DialogBase open={showConfirmDialog} onClose={handleCancel} title="Wand teilen bestätigen">
        <>
          <Typography variant="body1" gutterBottom>
            Möchten Sie die Wand wirklich teilen?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Folgende Einstellungen werden zurückgesetzt:
          </Typography>
          <List dense>
            {sideEffects.map((effect, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemText
                  primary={`${index + 1}. ${effect}`}
                  primaryTypographyProps={{ variant: 'body2' }}
                />
              </ListItem>
            ))}
          </List>
        </>
        <>
          <Button onClick={handleCancel}>Abbrechen</Button>
          <LoadingButton
            loading={isLoading}
            variant="contained"
            color="primary"
            onClick={handleConfirm}
            autoFocus
          >
            Bestätigen
          </LoadingButton>
        </>
      </DialogBase>
    </>
  )
}

export default SplitWallForm
